import { inject, injectable } from 'tsyringe';
import { BaseViewModel } from '@/shared/viewmodels/BaseViewModel';
import { IAnalyticsGatewayToken, type IAnalyticsGateway } from '@/shared/analytics/AnalyticsGateway';
import { ClassificationService } from '../classification/ClassificationService';
import { listedGovernancePolicies } from './PolicyDerivation';
import { compareIssues, type GovernanceIssue, type IssueStatus } from './PolicyIssues';
import { loadGovernanceIssueFeed } from './GovernanceIssueFeed';
import { UserLookupService, type UserOption } from '@/features/admin/UserLookupService';
import { GovernanceIssueStore } from './GovernanceIssueStore';
import { GovernanceIssuesService, type GovernanceIssueAttachmentContent, type GovernanceIssueDetail } from './GovernanceIssuesService';

/** Assignable user for the issue-assignee picker. */
export type AssigneeOption = UserOption;

/** A governance issue with its (overridable) triage state applied from the store. */
export interface TrackedIssue extends GovernanceIssue {
    status: IssueStatus;
    flagged: boolean;
    assigneeUserId: string | null;
}

/**
 * Backs the Governance "Issues" tab: one row per flagged conversation turn, unified across
 * both sources — policy-verdict hits and sensitivity-flag hits — each with its triage state
 * from {@link GovernanceIssueStore}. Clicking a row opens the detail drawer (conversation +
 * status/notes/audit).
 */
@injectable()
export class GovernanceIssuesViewModel extends BaseViewModel {
    loading = true;
    loaded = false;
    errorMessage: string | null = null;
    private base: GovernanceIssue[] = [];

    /** Detail-drawer state. */
    activeIssue: TrackedIssue | null = null;
    activeDetail: GovernanceIssueDetail | null = null;
    detailLoading = false;

    constructor(
        private readonly service: ClassificationService,
        @inject(IAnalyticsGatewayToken) private readonly gateway: IAnalyticsGateway,
        private readonly store: GovernanceIssueStore,
        private readonly issuesService: GovernanceIssuesService,
        private readonly userLookup: UserLookupService,
    ) {
        super();
    }

    /** Team users for the assignee picker. */
    get assigneeOptions(): AssigneeOption[] {
        return this.userLookup.options();
    }

    /** Resolve an auth user id to a readable email (falls back to the id if unknown). */
    emailFor(userId: string): string {
        return this.userLookup.emailFor(userId);
    }

    private tracked(issue: GovernanceIssue): TrackedIssue {
        return {
            ...issue,
            status: this.store.statusOf(issue.key),
            flagged: this.store.isFlagged(issue.key),
            assigneeUserId: this.store.assigneeOf(issue.key),
        };
    }

    get issues(): TrackedIssue[] {
        return this.base.map((issue) => this.tracked(issue)).sort(compareIssues);
    }

    get counts(): Record<IssueStatus, number> {
        const all = this.issues;
        return {
            open: all.filter((i) => i.status === 'open').length,
            investigating: all.filter((i) => i.status === 'investigating').length,
            resolved: all.filter((i) => i.status === 'resolved').length,
        };
    }

    async initialize(): Promise<void> {
        this.loading = true;
        this.errorMessage = null;
        this.notifyChange();
        try {
            await this.store.initialize();
            const all = await this.service.list();
            this.base = await loadGovernanceIssueFeed(this.gateway, listedGovernancePolicies(all));
            void this.userLookup.ready().then(() => this.notifyChange());
        } catch (error) {
            this.errorMessage = error instanceof Error ? error.message : 'Failed to load issues';
            this.base = [];
        } finally {
            this.loading = false;
            this.loaded = true;
            this.notifyChange();
        }
    }

    async setStatus(issue: GovernanceIssue, status: IssueStatus): Promise<void> {
        await this.store.setStatus(issue.kind, issue.ref, status);
        await this.refreshActiveDetail(issue.key);
    }

    async setFlag(issue: GovernanceIssue, flagged: boolean): Promise<void> {
        await this.store.setFlag(issue.kind, issue.ref, flagged);
        await this.refreshActiveDetail(issue.key);
    }

    async assign(issue: GovernanceIssue, assigneeUserId: string | null): Promise<void> {
        await this.store.assign(issue.kind, issue.ref, assigneeUserId);
        await this.refreshActiveDetail(issue.key);
    }

    async resolve(issue: GovernanceIssue, reason: string): Promise<void> {
        await this.store.resolve(issue.kind, issue.ref, reason);
        await this.refreshActiveDetail(issue.key);
    }

    async openIssue(issue: TrackedIssue): Promise<void> {
        this.activeIssue = issue;
        this.activeDetail = null;
        this.detailLoading = true;
        this.notifyChange();
        try {
            this.activeDetail = await this.issuesService.detail(issue.kind, issue.ref);
        } catch (error) {
            this.errorMessage = error instanceof Error ? error.message : 'Failed to load issue detail';
        } finally {
            this.detailLoading = false;
            this.notifyChange();
        }
    }

    closeIssue(): void {
        this.activeIssue = null;
        this.activeDetail = null;
        this.notifyChange();
    }

    async addNote(body: string): Promise<void> {
        if (!this.activeIssue) return;
        await this.issuesService.addNote(this.activeIssue.kind, this.activeIssue.ref, body);
        await this.reloadActiveDetail();
    }

    async addAttachment(file: File): Promise<void> {
        if (!this.activeIssue) return;
        const base64 = encodeBase64(await file.arrayBuffer());
        await this.issuesService.addAttachment(this.activeIssue.kind, this.activeIssue.ref, file.name, file.type || 'application/octet-stream', base64);
        await this.reloadActiveDetail();
    }

    fetchAttachment(attachmentId: string): Promise<GovernanceIssueAttachmentContent> {
        if (!this.activeIssue) return Promise.reject(new Error('No active issue'));
        return this.issuesService.attachment(this.activeIssue.kind, this.activeIssue.ref, attachmentId);
    }

    private async refreshActiveDetail(key: string): Promise<void> {
        if (this.activeIssue && this.activeIssue.key === key) {
            this.activeIssue = this.tracked(this.activeIssue);
            await this.reloadActiveDetail();
        }
    }

    private async reloadActiveDetail(): Promise<void> {
        if (!this.activeIssue) return;
        this.activeDetail = await this.issuesService.detail(this.activeIssue.kind, this.activeIssue.ref);
        this.notifyChange();
    }
}

/** Base64-encode raw bytes for upload (chunked to avoid call-stack limits on large files). */
function encodeBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    const chunk = 0x8000;
    for (let i = 0; i < bytes.length; i += chunk) {
        binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
    }
    return btoa(binary);
}
